import React, { useEffect, useMemo, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import momentPlugin from '@fullcalendar/moment'
import { PropsType } from '../../models/util'
import { Tooltip } from 'primereact/tooltip'
import { getContrast } from '../../utils/text-color'
import moment from 'moment'
import _ from 'lodash'

export interface MainCalendarProps extends PropsType {
    height?: number | 'auto' | 'parent' | (() => number)
    firstDay?: number
    eventClick?: (info?: any) => void
    elRef: any
    dateFilter?: { start_date?: Date; end_date?: Date }
    resourceName?: string
    resources: any[]
    events: any[]
    allEvents: any[]
    isYearView?: boolean
    isLiveEdit: boolean
    onResize: (info: any) => void
    onDragAndDrop: (info: any) => void
    eventRender?: (info: any) => void
    resourceRender?: (info: any) => void
    viewSkeletonRender?: (info: any) => void
    resourceGroup?: any
}

const MainCalendar = (props: MainCalendarProps) => {
    const {
        viewSkeletonRender,
        firstDay,
        eventClick,
        elRef,
        dateFilter,
        resourceName,
        resources,
        events,
        isLiveEdit,
        isYearView,
        onDragAndDrop,
        onResize,
        eventRender,
        resourceRender,
        height,
        resourceGroup,
        allEvents,
    } = props

    const [focusedEvent, setFocusedEvent] = useState<any>({})

    const initiativeListWWIds = useMemo(() => {
        if (!allEvents) {
            return []
        }

        const ID_WW_LOCALIZATION = JSON.parse(
            sessionStorage.getItem('ID_WW_LOCALIZATION')
        )
        return allEvents
            .filter((event) => event.subsidiary_id == ID_WW_LOCALIZATION)
            .map((event) => event.id)
    }, [allEvents])

    let calendarPlugins = [
        dayGridPlugin,
        resourceTimelinePlugin,
        interactionPlugin,
        momentPlugin,
        resourceTimeGridPlugin,
    ]

    const tooltipRef = useRef(undefined)

    useEffect(() => {
        if (dateFilter.start_date) {
            let calendarApi = elRef.current.getApi()
            calendarApi.gotoDate(dateFilter.start_date)
            calendarApi.gotoDate(dateFilter.start_date)
        }
    }, [dateFilter])

    useEffect(() => {
        let calendarApi = elRef.current.getApi()
        calendarApi.changeView(isYearView ? 'customYear' : 'customWeek')
    }, [isYearView])
    const _getTitleByEvent = (event) => {
        //Se è di tipo campaign
        if (event.brand_code && event.title_campaign) {
            return `${event.brand_code} - ${event.title_campaign}`
        }

        //Se è di tipo holiday
        if (event.title_holiday) {
            return event.title_holiday
        }

        //Se è di tipo promo
        if (event.title_promo) {
            return event.title_promo
        }

        //Se è di tipo initiative
        if (event.title) {
            return event.title
        }

        return ''
    }

    const startDateLabel = useMemo(() => {
        if (initiativeListWWIds.includes(focusedEvent.id)) {
            return `Start Date: ${focusedEvent.start_label}`
        }

        if (focusedEvent.visibilityId && !focusedEvent.toolId) {
            let eventList = events.filter(x => x.id == focusedEvent.id && x.segmentId == focusedEvent.segmentId)
            let start_label = ''
            eventList.forEach((element) => {
                if (start_label == '') {start_label = element.start}
                else {
                    let oldLabel = moment(start_label)
                    let newDate = moment(element.start)
                    if (oldLabel > newDate) {start_label = element.start}
                }
            });
            return `Start Date: ${moment
                .utc(start_label)
                .format('YYYY-MM-DD')}`
        }

        return `Start Date: ${moment
            .utc(focusedEvent.start || focusedEvent.start_label)
            .format('YYYY-MM-DD')}`
    }, [focusedEvent, initiativeListWWIds])

    const endDateLabel = useMemo(() => {
        if (initiativeListWWIds.includes(focusedEvent.id)) {
            return `End Date: ${focusedEvent.end_label}`
        }

        if (focusedEvent.visibilityId && !focusedEvent.toolId) {
            let eventList = events.filter(x => x.id == focusedEvent.id && x.segmentId == focusedEvent.segmentId)
            let end_label = ''
            eventList.forEach((element) => {
                if (end_label == '') {end_label = element.end}
                else {
                    let oldLabel = moment(end_label)
                    let newDate = moment(element.end)
                    if (newDate > oldLabel) {end_label = element.end}
                }
            });
            return `End Date: ${moment
                .utc(end_label)
                .format('YYYY-MM-DD')}`
        }

        return `End Date: ${moment
            .utc(focusedEvent.end || focusedEvent.end_label)
            .format('YYYY-MM-DD')}`
    }, [focusedEvent, initiativeListWWIds])

    useEffect(() => {
        console.log(focusedEvent)
    }, [focusedEvent])

    return (
        <div className="wardrobe-main-calendar">
            <FullCalendar
                schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
                displayEventTime={false}
                defaultView="customYear" //"resourceTimelineYear"
                plugins={calendarPlugins}
                firstDay={firstDay ? firstDay : 0} // first day of week
                //TODO test tooltip
                eventMouseEnter={(info: any) => {
                    // info.el.setAttribute('data-pr-tooltip', info.event.title)
                    const newFocusedEvent = {
                        id: info.event.id,
                        title: info.event.title,
                        ...info?.event?.extendedProps,
                    }
                    if (!_.isEqual(focusedEvent, newFocusedEvent)) {
                        setFocusedEvent(newFocusedEvent)
                    }
                    tooltipRef?.current?.updateTargetEvents()
                }}
                eventClick={eventClick}
                // rerenderDelay={500} // timeout visualizzazione calendario
                ref={elRef}
                resourceLabelText={resourceName}
                filterResourcesWithEvents={true} // only resources that have associated events will be displayed.
                resourcesInitiallyExpanded={false} // struttura esplosa - asse y
                resourceGroupField={resourceGroup === null ? null : 'sezione'}
                header={false}
                validRange={{
                    start: dateFilter?.start_date,
                    end: dateFilter?.end_date,
                }}
                hiddenDays={[1, 2, 3, 4, 5]}
                weekNumbers={true}
                weekNumbersWithinDays={false}
                nowIndicator={true}
                height={height ? height : 'auto'}
                resourceAreaWidth="15%"
                views={{
                    customWeek: {
                        type: 'resourceTimelineMonth',
                        duration: { month: 12 },
                        slotLabelFormat: [
                            { month: 'long' }, // top level of text
                            { week: 'short' },
                            'D MMM ', // lower level of text
                        ],
                        hiddenDays: [],
                    },
                    customYear: {
                        type: 'resourceTimelineYear',
                        duration: { month: 12 },
                        slotDuration: { days: 7 },
                        slotLabelFormat: [
                            { month: 'long' }, // top level of text
                            { week: 'short' },
                            'D MMM ', // lower level of text
                        ],
                        hiddenDays: [],
                    },
                }}
                resourceRender={resourceRender}
                viewSkeletonRender={viewSkeletonRender}
                eventRender={(info) => {
                    let eventElStyle =
                        info.el.querySelector('.fc-title-wrap').parentElement
                            .style
                    eventElStyle.color = getContrast(info.event.backgroundColor)

                    if (eventRender) eventRender(info)
                    // ADD ICON to event label
                    // TODO replace <a> tag, add conditions
                    // let eventEl = info.el.querySelector('.fc-title-wrap')
                    // let eventID = info.event.id
                    // let link = document.createElement('a')
                    // let linkIcon = document.createElement('i')
                    // link.id = eventID
                    // link.classList.add('fc-sticky')
                    // linkIcon.classList.add('pi', 'pi-circle-on', 'red-icon')
                    // link.appendChild(linkIcon)
                    // eventEl.appendChild(link)
                }}
                // dayRender={function (info) {
                //     let cellDay = new Date(info.date)
                // }}
                resources={resources}
                rerenderDelay={1}
                events={events.map((event) => ({
                    ...event,
                    title: _getTitleByEvent(event),
                }))}
                // edit mode
                editable={isLiveEdit} // enable draggable events
                eventDurationEditable={isLiveEdit}
                eventResourceEditable={isLiveEdit}
                eventResize={onResize}
                eventDrop={onDragAndDrop}
                eventOverlap={true}
            />
            <Tooltip
                target=".fc-event"
                ref={tooltipRef}
                position="top"
                my="left"
                mouseTrack>
                <div>
                    <p style={{ color: 'var(--lux-white' }}>
                        {_getTitleByEvent(focusedEvent)}
                        <br />
                        {startDateLabel}
                        <br />
                        {endDateLabel}
                    </p>
                </div>
            </Tooltip>
        </div>
    )
}

export default MainCalendar
